/** Reusable shell and reading-width constraints. */
export const layout = {
  contentReadable: '46rem',
  contentStandard: '72rem',
  /** A touch wider than `standard` for image-dense pages (e.g. the public
   * profile) that need two large media cards side by side without the shell's
   * full width. */
  contentComfortable: '78rem',
  contentWide: '90rem',
  sidebar: '18rem',
  contextRail: '15rem',
  headerHeight: '4.5rem',
  touchTarget: '2.75rem',
} as const;

export type LayoutToken = keyof typeof layout;

/** Decorative detail is intentionally reduced as screens become denser. */
export const ornamentation = {
  none: '0',
  restrained: '1',
  framed: '2',
} as const;

export type OrnamentationToken = keyof typeof ornamentation;

/**
 * On-screen keyboard geometry (docs/UI_GUIDELINES.md §8). One canonical set
 * of key/gap/typography values so no game screen invents its own keyboard
 * dimensions — the shared `apps/web/src/components/Keyboard.tsx` component is
 * the only consumer. `keyHeight`/`fontSize` use `clamp()` so keys stay
 * tappable from 320px up without horizontal overflow and without a
 * device-specific layout.
 */
export const keyboard = {
  maxWidth: '30rem',
  columnGap: '0.25rem',
  rowGap: '0.4rem',
  keyMinWidth: '0',
  // Height is the reliably-tappable dimension and is preserved at every
  // supported width (>= 44px). Width scales down with the viewport so a
  // 10-key row never forces horizontal overflow and rows stay aligned.
  keyHeight: 'clamp(2.75rem, 13vw, 3.4rem)',
  radius: '0.4rem',
  fontSize: 'clamp(0.8125rem, 3.4vw, 1rem)',
  actionFontSize: 'clamp(0.6875rem, 2.7vw, 0.8125rem)',
} as const;

export type KeyboardToken = keyof typeof keyboard;
